import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const fmt = (n, d = 0) => n == null ? '—' : Number(n).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtCNY = n => n == null ? '—' : `¥${fmt(n)}`;
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const REC_COLORS = { BUY_NOW: 'red', BUY_TRANCHE: 'amb', HOLD: 'grn', WAIT: 'blu' };
const PAGE_SIZE = 25;

export default function History({ addToast }) {
  const [runs, setRuns] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({ completedOutcomes: 0, avgError: 0, avgQuality: 0, outcomeCount: 0, qualityCount: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [outcome, setOutcome] = useState({ actual_pp_7d: '', outcome_notes: '' });
  const [savingOutcome, setSavingOutcome] = useState(false);

  // Fetch aggregate stats from ALL rows — independent of pagination.
  const fetchStats = useCallback(async () => {
    const [countRes, statsRes] = await Promise.all([
      supabase.from('pp_map_runs').select('*', { count: 'exact', head: true }),
      supabase.from('pp_map_runs').select('actual_pp_7d, forecast_error, recommendation_quality'),
    ]);
    setTotalCount(countRes.count || 0);
    if (!statsRes.error && statsRes.data) {
      const all = statsRes.data;
      const withError   = all.filter(r => r.forecast_error != null);
      const withQuality = all.filter(r => r.recommendation_quality != null);
      setStats({
        completedOutcomes: all.filter(r => r.actual_pp_7d != null).length,
        avgError: withError.length > 0
          ? withError.reduce((s, r) => s + r.forecast_error, 0) / withError.length
          : 0,
        avgQuality: withQuality.length > 0
          ? withQuality.reduce((s, r) => s + r.recommendation_quality, 0) / withQuality.length
          : 0,
        outcomeCount:  withError.length,
        qualityCount:  withQuality.length,
      });
    }
  }, []);

  // Fetch one page of rows.
  const fetchPage = useCallback(async (pageNum) => {
    setLoading(true);
    try {
      const from = (pageNum - 1) * PAGE_SIZE;
      const { data, error } = await supabase
        .from('pp_map_runs')
        .select('*')
        .order('run_date', { ascending: false })
        .range(from, from + PAGE_SIZE - 1);
      if (error) throw error;
      setRuns(data || []);
    } catch (e) {
      console.error(e);
      addToast('Could not load history — check Supabase connection', 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    fetchStats();
    fetchPage(1);
  }, [fetchStats, fetchPage]);

  const goToPage = (newPage) => {
    setPage(newPage);
    fetchPage(newPage);
  };

  const saveOutcome = async (runId) => {
    if (!outcome.actual_pp_7d) return;
    setSavingOutcome(true);
    try {
      const run = runs.find(r => r.id === runId);
      const forecastError = run
        ? Math.abs(parseFloat(outcome.actual_pp_7d) - run.pp_expected_7d) / run.pp_expected_7d * 100
        : null;
      const { error } = await supabase.from('pp_map_runs').update({
        actual_pp_7d:  parseFloat(outcome.actual_pp_7d),
        outcome_notes: outcome.outcome_notes,
        forecast_error: forecastError ? Math.round(forecastError * 10) / 10 : null,
      }).eq('id', runId);
      if (error) throw error;
      addToast('Outcome saved — learning loop updated', 'success');
      fetchStats();
      fetchPage(page);
      setSelected(null);
    } catch (e) {
      addToast('Failed to save outcome', 'error');
    } finally {
      setSavingOutcome(false);
    }
  };

  const totalPages   = Math.ceil(totalCount / PAGE_SIZE);
  const showFrom     = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const showTo       = Math.min(page * PAGE_SIZE, totalCount);
  const pendingCount = runs.filter(r => !r.actual_pp_7d && new Date(r.run_date) < new Date(Date.now() - 7 * 86400000)).length;

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>Loading history...</div>;

  return (
    <div>
      <div className="page-hdr">
        <h2>Model History & Learning Loop</h2>
        <p>Each saved run is stored in Supabase. Enter actual PP prices 7 days later to close the learning loop and track forecast accuracy.</p>
      </div>

      {/* Stats — computed from ALL rows, not just current page */}
      <div className="g4 mb-16">
        {[
          { label: 'Total runs',          val: totalCount, color: 'blu', sub: null },
          { label: 'Completed outcomes',  val: stats.completedOutcomes, color: 'grn', sub: null },
          { label: 'Avg forecast error',  val: stats.avgError > 0 ? `${fmt(stats.avgError, 1)}%` : '—', color: stats.avgError > 10 ? 'red' : stats.avgError > 5 ? 'amb' : 'grn', sub: null },
          { label: 'Avg forecast accuracy', val: stats.qualityCount > 0 ? `${fmt(stats.avgQuality * 100, 1)}%` : '—', color: 'blu', sub: stats.qualityCount > 0 ? `rec. quality · N=${stats.qualityCount} runs` : null },
        ].map(s => (
          <div key={s.label} className="card" style={{ borderTop: `3px solid var(--${s.color})` }}>
            <div className="label-mono mb-4">{s.label}</div>
            <div style={{ fontFamily: 'var(--head)', fontSize: 28, fontWeight: 800, color: `var(--${s.color})` }}>{s.val}</div>
            {s.sub && <div style={{ fontSize: 10, color: 'var(--t2)', marginTop: 3 }}>{s.sub}</div>}
          </div>
        ))}
      </div>

      {/* Pending outcomes — checked against current page */}
      {pendingCount > 0 && (
        <div className="alert alert-amb mb-16">
          <span className="alert-icon">⏰</span>
          <div className="alert-body">
            <strong>{pendingCount} runs</strong> on this page are ready for outcome entry (7+ days old). Enter actual PP prices to improve model accuracy.
          </div>
        </div>
      )}

      {totalCount === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text3)' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📈</div>
          <div style={{ fontFamily: 'var(--head)', fontSize: 18, fontWeight: 700, marginBottom: 6 }}>No runs saved yet</div>
          <div style={{ fontSize: 13 }}>Run the model on the Dashboard and click "Save Run" to start the learning loop.</div>
        </div>
      )}

      {/* Run Table */}
      {runs.length > 0 && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '8px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--t2)' }}>
              Showing {showFrom}–{showTo} of {totalCount} runs
            </span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>STI</th>
                  <th>MSI</th>
                  <th>Recommendation</th>
                  <th>PP Spot</th>
                  <th>PP Expected 7d</th>
                  <th>Actual PP 7d</th>
                  <th>Error</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {runs.map(r => {
                  const color = REC_COLORS[r.recommendation] || 'blu';
                  const needsOutcome = !r.actual_pp_7d && new Date(r.run_date) < new Date(Date.now() - 7 * 86400000);
                  return (
                    <tr key={r.id}>
                      <td style={{ whiteSpace: 'nowrap', fontFamily: 'var(--mono)', fontSize: 11 }}>{fmtDate(r.run_date)}</td>
                      <td>
                        <span style={{ fontFamily: 'var(--mono)', fontWeight: 700, color: r.sti_score >= 75 ? 'var(--red)' : r.sti_score >= 55 ? 'var(--amb)' : 'var(--grn)' }}>
                          {fmt(r.sti_score)}
                        </span>
                      </td>
                      <td>
                        <span style={{ fontFamily: 'var(--mono)', fontWeight: 700, color: r.msi_score >= 70 ? 'var(--red)' : r.msi_score >= 50 ? 'var(--amb)' : 'var(--grn)' }}>
                          {fmt(r.msi_score)}
                        </span>
                      </td>
                      <td><span className={`badge badge-${color}`}>{r.recommendation}</span></td>
                      <td style={{ fontFamily: 'var(--mono)', fontSize: 11 }}>{fmtCNY(r.pp_spot_qingdao)}</td>
                      <td style={{ fontFamily: 'var(--mono)', fontSize: 11 }}>{fmtCNY(r.pp_expected_7d)}</td>
                      <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: r.actual_pp_7d ? 'var(--text)' : 'var(--text3)' }}>
                        {r.actual_pp_7d ? fmtCNY(r.actual_pp_7d) : '—'}
                      </td>
                      <td>
                        {r.forecast_error != null ? (
                          <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: r.forecast_error > 10 ? 'var(--red)' : r.forecast_error > 5 ? 'var(--amb)' : 'var(--grn)', fontWeight: 700 }}>
                            {fmt(r.forecast_error, 1)}%
                          </span>
                        ) : needsOutcome ? <span className="badge badge-amb">ENTER</span> : <span style={{ color: 'var(--text3)' }}>—</span>}
                      </td>
                      <td>
                        {needsOutcome && (
                          <button className="btn btn-secondary btn-sm" onClick={() => { setSelected(r); setOutcome({ actual_pp_7d: '', outcome_notes: '' }); }}>
                            + Outcome
                          </button>
                        )}
                        {!needsOutcome && !r.actual_pp_7d && (
                          <span style={{ fontSize: 11, color: 'var(--text3)' }}>Pending 7d</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => goToPage(page - 1)}
                disabled={page === 1}
              >
                ← Previous
              </button>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--t2)' }}>
                Page {page} of {totalPages}
              </span>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages}
              >
                Next →
              </button>
            </div>
          )}
        </div>
      )}

      {/* Outcome Modal */}
      {selected && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(13,17,23,.5)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 24, maxWidth: 440, width: '100%', boxShadow: '0 20px 40px rgba(0,0,0,.2)' }}>
            <div style={{ fontFamily: 'var(--head)', fontSize: 18, fontWeight: 800, marginBottom: 6 }}>Enter Outcome</div>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 16 }}>
              Run from <strong>{fmtDate(selected.run_date)}</strong> · Forecast: <strong>{fmtCNY(selected.pp_expected_7d)}/MT</strong>
            </div>
            <div className="form-group">
              <div className="form-label"><span className="form-label-text">Actual PP spot 7 days later (¥/MT)</span></div>
              <input type="number" className="form-input" step="50" value={outcome.actual_pp_7d} onChange={e => setOutcome(o => ({ ...o, actual_pp_7d: e.target.value }))} placeholder="e.g. 10200" />
            </div>
            <div className="form-group">
              <div className="form-label"><span className="form-label-text">Notes (optional)</span></div>
              <textarea className="form-input" rows={2} value={outcome.outcome_notes} onChange={e => setOutcome(o => ({ ...o, outcome_notes: e.target.value }))} placeholder="What happened? What drove the price move?" />
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button className="btn btn-primary" disabled={savingOutcome || !outcome.actual_pp_7d} onClick={() => saveOutcome(selected.id)}>
                {savingOutcome ? 'Saving...' : 'Save Outcome'}
              </button>
              <button className="btn btn-secondary" onClick={() => setSelected(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
